/**
 * The resident settings form's state, composed from one hook per section.
 *
 * What is left here is only what spans them: the rest of the stored configuration, what stops a
 * save, and the save itself. Each section's own rules live with the section —
 * `useIndoorGeofencing`, `useOutdoorGeofencing`, `useGpsImpact` — because they are read far more
 * often than they are read together, and the one wiring between them is explicit below: an allow
 * list requires outdoor cover, and outdoor cover is what makes the GPS interval worth warning
 * about.
 *
 * The five silent corrections live in those three, each commented where it happens. They preview a
 * coercion the backend performs anyway, which is why they will be dropped rather than ported when
 * this page is rendered from the configuration service's own definition.
 */

import * as React from 'react';
import { OutdoorGeofencingMode, ProfileConfiguration } from '@ixicare/resources';
import { getDefaultStateReducer } from '~/utils/reducer';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { useIndoorGeofencing } from './useIndoorGeofencing';
import { useOutdoorGeofencing } from './useOutdoorGeofencing';
import { GPS_OFF, useGpsImpact } from './useGpsImpact';
import { useResidentSite } from './useResidentSite';

/**
 * Keys a save must not round-trip out of the loaded configuration.
 *
 * `periodicGpsPeriod` is the one that matters: the device *reports* it, a sysadmin sets it, and
 * spreading it back would store it as a resident setting the carer never chose. The perimeter is
 * held out for symmetry — `submit` writes it explicitly either way, so it would be overwritten
 * rather than leak.
 *
 * Both were read here until the sections that own them took over; what is left is only the
 * omission.
 */
const NOT_ROUND_TRIPPED = ['outdoorGeofencingGeo', 'periodicGpsPeriod'] as const;

/**
 * An interval, as a person reads it. Used for the device's own GPS interval and the one a save
 * would write it to -- neither of which the definition describes, since that parameter is set in
 * the device configuration form and can hold any number.
 *
 * Not for the check-in options: the definition labels those, and did not when this existed.
 */
export const formatSeconds = (seconds: number) => {
  if (seconds < 60) return `${seconds} sec`;
  return `${seconds / 60} min`;
};

export interface ResidentConfigFormInput {
  configuration?: ProfileConfiguration;
  profile: UserProfileEntry;
  siteUuid?: string;
  onSubmit: (data: ProfileConfiguration) => void;
}

export const useResidentConfigForm = ({ configuration, profile, siteUuid, onSubmit }: ResidentConfigFormInput) => {
  // Everything the geofencing sections do not own, held as the save will spread it back.
  const initial = { ...(configuration ?? {}) };
  for (const key of NOT_ROUND_TRIPPED) delete initial[key];
  const [values, dispatch] = React.useReducer(getDefaultStateReducer<ProfileConfiguration>(), initial);

  // Everything the page needs to know about the resident's site, fetched once. The controls
  // below take it as props rather than asking for it themselves.
  const site = useResidentSite(siteUuid);

  const indoor = useIndoorGeofencing(configuration, siteUuid);
  const outdoor = useOutdoorGeofencing(configuration, profile, indoor.requiresOutdoorCover);
  const gps = useGpsImpact(configuration, outdoor.on, values.indoorCheckinFrequency);

  // Every applicable problem, not just the first: with two independent sections a carer can have
  // both wrong, and fixing one only to be told about the other is a bad loop.
  const problems: string[] = [];
  if (indoor.enabled && indoor.zones.length === 0) {
    problems.push(`profile-configuration.geofence.indoor.${indoor.polarity}.zonesRequired`);
  } else if (indoor.enabled && indoor.selectsEveryZone) {
    problems.push(`geofence.creator.${indoor.polarity}.allZonesSelected`);
  }
  if (outdoor.mode === OutdoorGeofencingMode.Geo && !outdoor.hasPerimeter) {
    problems.push('profile-configuration.geofence.outdoor.perimeterRequired');
  }

  const submit = React.useCallback(
    (stopGpsFixes: boolean) => {
      // All four geofencing keys are written explicitly. `values` still carries whatever the loaded
      // configuration had, so anything left out here would round-trip stale - a stored block list
      // would survive a switch to allow and read as the opposite of what the form shows.
      const data: ProfileConfiguration = {
        ...values,
        indoorGeofencingEnabled: indoor.enabled,
        indoorGeofencingMode: indoor.polarity,
        indoorGeofencingZones: indoor.enabled ? indoor.zones : [],
        // Sent on every save, null included: leaving it out would keep a preset the form has
        // already stopped showing, and the alarm sender would go on following it unseen.
        indoorGeofencingPreset: indoor.enabled ? indoor.presetId : null,
        outdoorGeofencingMode: outdoor.mode,
        outdoorGeofencingGeo: outdoor.mode === OutdoorGeofencingMode.Geo ? outdoor.geo : undefined,
      };
      if (stopGpsFixes) data.periodicGpsPeriod = GPS_OFF;
      onSubmit(data);
    },
    [values, indoor.enabled, indoor.polarity, indoor.zones, indoor.presetId, outdoor.mode, outdoor.geo]
  );

  const handleSubmit = React.useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (problems.length) return;
      // Asked at the save that causes it rather than when the checkbox is unticked: unticking and
      // reticking would otherwise leave an answer to a question that no longer applies.
      if (gps.stopsCover) {
        gps.setAsking(true);
        return;
      }
      submit(false);
    },
    [problems.length, gps.stopsCover, submit]
  );

  return { values, dispatch, site, indoor, outdoor, gps, problems, submit, handleSubmit };
};
